/**
 * SlashCommandMenu Component
 * Notion-style dropdown menu for slash commands
 */

import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';

// Command props type for slash commands
export interface CommandProps {
    editor: any;
    range: any;
}

export interface SlashMenuItem {
    title: string;
    description: string;
    icon: string;
    section: 'AI' | 'Style';
    command: (props: CommandProps) => void;
}

interface SlashCommandMenuProps {
    items: SlashMenuItem[];
    command: (item: SlashMenuItem) => void;
}

const SlashCommandMenu = forwardRef<any, SlashCommandMenuProps>((props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = useCallback(
        (index: number) => {
            const item = props.items[index];
            if (item) {
                props.command(item);
            }
        },
        [props]
    );

    const upHandler = useCallback(() => {
        setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
    }, [props.items.length, selectedIndex]);

    const downHandler = useCallback(() => {
        setSelectedIndex((selectedIndex + 1) % props.items.length);
    }, [props.items.length, selectedIndex]);

    const enterHandler = useCallback(() => {
        selectItem(selectedIndex);
    }, [selectItem, selectedIndex]);

    useEffect(() => setSelectedIndex(0), [props.items]);

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
            if (event.key === 'ArrowUp') {
                upHandler();
                return true;
            }

            if (event.key === 'ArrowDown') {
                downHandler();
                return true;
            }

            if (event.key === 'Enter') {
                enterHandler();
                return true;
            }

            return false;
        },
    }));

    // Group items by section
    const aiItems = props.items.filter(item => item.section === 'AI');
    const styleItems = props.items.filter(item => item.section === 'Style');

    const getGlobalIndex = (section: 'AI' | 'Style', localIndex: number) => {
        if (section === 'AI') return localIndex;
        return aiItems.length + localIndex;
    };

    return (
        <div className="slash-command-menu">
            {aiItems.length > 0 && (
                <div className="slash-section">
                    {aiItems.map((item, index) => {
                        const globalIndex = getGlobalIndex('AI', index);
                        return (
                            <button
                                key={item.title}
                                className={`slash-item ${globalIndex === selectedIndex ? 'is-selected' : ''}`}
                                onClick={() => selectItem(globalIndex)}
                            >
                                <span className="slash-item-icon">{item.icon}</span>
                                <span className="slash-item-title">{item.title}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {styleItems.length > 0 && (
                <div className="slash-section">
                    {styleItems.map((item, index) => {
                        const globalIndex = getGlobalIndex('Style', index);
                        return (
                            <button
                                key={item.title}
                                className={`slash-item ${globalIndex === selectedIndex ? 'is-selected' : ''}`}
                                onClick={() => selectItem(globalIndex)}
                            >
                                <span className="slash-item-icon">{item.icon}</span>
                                <span className="slash-item-title">{item.title}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {props.items.length === 0 && (
                <div className="slash-empty">No results found</div>
            )}

            <style>{`
        .slash-command-menu {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05);
          min-width: 220px;
          max-height: 400px;
          overflow-y: auto;
          padding: 8px;
        }

        .slash-section {
          margin-bottom: 4px;
        }

        .slash-section:last-child {
          margin-bottom: 0;
        }

        .slash-section-title {
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 8px 12px 4px;
        }

        .slash-item {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 10px 12px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          text-align: left;
          transition: background 0.15s ease;
        }

        .slash-item:hover,
        .slash-item.is-selected {
          background: #f3f4f6;
        }

        .slash-item-icon {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: #6b7280;
          flex-shrink: 0;
        }

        .slash-item-title {
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
        }

        .slash-empty {
          padding: 16px;
          text-align: center;
          color: #9ca3af;
          font-size: 13px;
        }

        /* Tippy theme */
        .tippy-box[data-theme~='slash-menu'] {
          background: transparent;
          padding: 0;
        }

        .tippy-box[data-theme~='slash-menu'] .tippy-content {
          padding: 0;
        }
      `}</style>
        </div>
    );
});

SlashCommandMenu.displayName = 'SlashCommandMenu';

export default SlashCommandMenu;
